import { useCallback, useEffect, useRef, useSyncExternalStore } from "react";
import { useQuery } from "@tanstack/react-query";
import { matchPath, useLocation } from "react-router";
import type { BuildingSummary } from "@klantpunt/shared";
import { api } from "./api";
import { useMe } from "./auth";

/**
 * Het gebouw waarin je werkt (gebouwkiezer in de bovenbalk). Per gebruiker en organisatie onthouden op dit
 * apparaat, net als de klant (lib/clientContext.ts).
 * Op een gebouwpagina (`/gebouwen/:id/…`) is het altijd dat gebouw; op Gebouwen (`/`) is het "Alle
 * gebouwen". Pagina's die ook over alle gebouwen kunnen gaan (MJOP, Conditie, Meterstanden) volgen de keuze.
 */
const keyFor = (scope: string) => `kp-building-${scope}`;
const listeners = new Set<() => void>();
const read = (scope: string | undefined) => {
  if (!scope) return null;
  try {
    return localStorage.getItem(keyFor(scope)) || null;
  } catch {
    return memory.get(scope) ?? null;
  }
};
/** Zonder opslag (privévenster): alleen in dit tabblad. */
const memory = new Map<string, string | null>();
function write(scope: string, id: string | null) {
  memory.set(scope, id);
  try {
    if (id) localStorage.setItem(keyFor(scope), id);
    else localStorage.removeItem(keyFor(scope));
  } catch {
    /* geen opslag */
  }
  listeners.forEach((l) => l());
}
const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => listeners.delete(l);
};

/** Gebouw-id uit het pad, of null buiten een gebouwpagina. */
export const buildingIdFromPath = (pathname: string) =>
  matchPath("/gebouwen/:id/*", pathname)?.params.id ?? matchPath("/gebouwen/:id", pathname)?.params.id ?? null;

export const buildingsKey = (orgId: string | undefined) => ["buildings", orgId] as const;

export function useBuildings() {
  const { data: me } = useMe();
  return useQuery({
    queryKey: buildingsKey(me?.org?.id),
    queryFn: () => api<BuildingSummary[]>("/buildings"),
    enabled: Boolean(me?.org),
  });
}

export function useBuildingContext() {
  const { data: me } = useMe();
  // Opslagsleutel: gebruiker + organisatie (op een gedeeld apparaat neemt een collega jouw gebouw niet over).
  const scope = me?.org ? `${me.user.id}-${me.org.id}` : undefined;
  const { pathname } = useLocation();
  const stored = useSyncExternalStore(subscribe, () => read(scope));
  const fromPath = buildingIdFromPath(pathname);
  const buildings = useBuildings();
  const raw = fromPath ?? (pathname === "/" ? null : stored);
  // Een bewaard gebouw dat niet (meer) bestaat, telt niet.
  const id = raw && buildings.data && !fromPath && !buildings.data.some((b) => b.id === raw) ? null : raw;
  const building = buildings.data?.find((b) => b.id === id) ?? null;

  // Wie een gebouw opent (of Gebouwen), kiest daarmee ook voor de andere pagina's. Alleen bij een
  // nieuw pad: een keuze in de kiezer gaat voor, ook als de navigatie erna nog moet volgen.
  const seen = useRef<string | null>(null);
  useEffect(() => {
    if (!scope || seen.current === pathname) return;
    seen.current = pathname;
    if (fromPath && fromPath !== read(scope)) write(scope, fromPath);
    if (pathname === "/" && read(scope)) write(scope, null);
  }, [scope, fromPath, pathname]);

  const setBuilding = useCallback((next: string | null) => scope && write(scope, next), [scope]);
  return { id, building, buildings: buildings.data, setBuilding };
}
